import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/redux';
// mergePrices removed — poll dispatch handled by WS now
import { startGoldPricePoll } from '@/services/goldPrice.service';
import { useGoldPriceSocket } from './useGoldPriceSocket';

/**
 * Singleton subscription hook — call EXACTLY ONCE at app root level
 * (currently wired in App.tsx via GoldPriceProvider).
 *
 * Opens the WebSocket connection and manages the 30s polling fallback.
 * Polling is skipped when the WebSocket is connected to avoid oscillation
 * (I3) and to prevent polling from overwriting WS-set fields.
 *
 * Multiple instances would create duplicate connections — keep to ONE call site.
 */
export function useGoldPriceSubscription(): void {
  const dispatch = useDispatch<AppDispatch>();
  const isConnected = useSelector((s: RootState) => s.goldPrice.isConnected);
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);

  // WebSocket — sets prices + isConnected in Redux via setPrices/setConnected.
  useGoldPriceSocket();

  useEffect(() => {
    // Polling fallback — backup when WS is not connected / dropped / TV mode.
    // Skip entirely when not authenticated: /gold-prices/current requires a
    // Bearer token, calling it pre-login returns 401 MISSING_TOKEN.
    if (!isAuthenticated) return;

    // Uses mergePrices (not setPrices) so partial-shape polling responses
    // never erase fields like overrideApplied that WS may have set.
    const cleanup = startGoldPricePoll(30_000);
    return cleanup;
  }, [dispatch, isConnected, isAuthenticated]);
}

/**
 * Pure read-only selector — safe to call from any number of components.
 * Does NOT open WebSocket or polling connections.
 *
 * Usage:
 *   const { prices, lastUpdated, isConnected } = useGoldPrice();
 */
export function useGoldPrice() {
  const { prices, lastUpdated, isConnected } = useSelector((s: RootState) => s.goldPrice);
  return { prices, lastUpdated, isConnected };
}
